import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Phone, Globe } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/ben2d0xw_Photoroom_20251219_184057.png";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t, isRTL } = useLanguage();

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-lg border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <nav className={`flex items-center justify-between h-20 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="TDABYR ALMSTQBL"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className={`hidden lg:flex items-center gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium transition-colors hover:text-uae-red ${
                  isActive(link.path)
                    ? 'text-uae-red'
                    : 'text-foreground/80'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-uae-red rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Buttons + Language Toggle */}
          <div className={`hidden lg:flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="default"
              className="gap-2 text-foreground/80 hover:text-foreground"
              onClick={toggleLanguage}
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'عربي' : 'EN'}
            </Button>
            
            <Button
              variant="outlineGreen"
              size="default"
              className="gap-2"
              onClick={() => window.open('tel:+97167796889', '_self')}
            >
              <Phone className="w-4 h-4" />
              {t('nav.callUs')}
            </Button>
            <Button
              variant="uaeRed"
              size="default"
              onClick={() => window.open('https://wa.me/97167796889', '_blank')}
            >
              {t('nav.getQuote')}
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <div className={`lg:hidden flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Mobile Language Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="text-foreground/80"
            >
              <Globe className="w-5 h-5" />
            </Button>
            
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side={isRTL ? "left" : "right"} className="w-[300px] bg-background border-l border-border">
                <div className="flex flex-col h-full pt-8">
                  {/* Mobile Logo */}
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="mb-8">
                    <img
                      src={LOGO_URL}
                      alt="TDABYR ALMSTQBL"
                      className="h-12 w-auto"
                    />
                  </Link>

                  {/* Language Indicator */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                    <span className="text-sm text-muted-foreground">
                      {language === 'en' ? 'Language' : 'اللغة'}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleLanguage}
                      className="gap-2"
                    >
                      <Globe className="w-4 h-4" />
                      {language === 'en' ? 'عربي' : 'English'}
                    </Button>
                  </div>

                  {/* Mobile Nav Links */}
                  <div className="flex flex-col gap-4">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`text-lg font-medium py-2 border-b border-border transition-colors ${
                          isActive(link.path)
                            ? 'text-uae-red border-uae-red'
                            : 'text-foreground/80 hover:text-uae-red'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>

                  {/* Mobile CTAs */}
                  <div className="mt-auto pb-8 flex flex-col gap-4">
                    <Button
                      variant="outlineGreen"
                      size="lg"
                      className="w-full gap-2"
                      onClick={() => window.open('tel:+97167796889', '_self')}
                    >
                      <Phone className="w-5 h-5" />
                      {t('nav.callUs')}
                    </Button>
                    <Button
                      variant="uaeRed"
                      size="lg"
                      className="w-full"
                      onClick={() => window.open('https://wa.me/97167796889', '_blank')}
                    >
                      {t('nav.getQuote')}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
